import { BarList, LineChart, StatTile } from '@olytic/foundation';
import {
  AskGrid,
  OptionCard,
  PhaseTimeline,
  ProposalContents,
  ProposalHeader,
  ProposalList,
  ProposalSection,
  ProposalTable,
  type ProposalTableColumn,
  QuoteLines,
  TotalsStack,
} from '@olytic/foundation/cpq';
import { type ReactNode, useEffect, useRef } from 'react';

import olyticMark from '../assets/olytic-inclined-ascent.svg';
import './proposal-print.css';
import { formatCurrency, formatDate } from '../objects/format';
import { boldRuns, type BusinessCase, fillTokens, linesOf } from './businessCase';
import { investmentSummary, recurringInvoices } from './investment';
import { orderSections } from './orderView';
import { clearPrintFit, fitForPrint } from './printFit';
import { inAppendix, parseBody, type ProposalLayout, type SectionKey, type SectionRef, titleOf } from './proposal';
import type { QuoteBuilder } from './quotesApi';
import { monthlyCostOf, roiLead } from './RoiTab';
import { milestoneAmounts, scheduleOf, type Terms, type Timeline } from '@quote-math/terms';
import { impactOf, type Metric, roiByYear, roiModel, type ValueModel, valueTotals, withScenario } from '@quote-math/valueModel';

const money = (n: number) => formatCurrency(Math.round(n));
const round1k = (n: number) => Math.round(n / 1000) * 1000;
const one1 = (n: number) => Math.round(n * 10) / 10;

const lead = 'm-0 max-w-3xl text-[15px] leading-relaxed text-[var(--fg)]';
const note = 'm-0 text-[13px] leading-[1.5] text-[var(--fg-muted)]';
const subhead = 'm-0 mb-1.5 mt-4 text-[13px] font-strong text-[var(--fg)]';
const tile = 'rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]';

function fmtMetric(m: Metric): string {
  if (m.unit === '$ / yr') return `${money(m.value)} a year`;
  return `${m.value.toLocaleString('en-US')} ${m.unit.replace(' / yr', ' a year')}`;
}

/** Months to pay back if the benefits came to `scale` of the expected case. */
function paybackAt(model: ValueModel, oneTime: number, monthlyCost: number, scale: number): number | null {
  const gross = valueTotals(model).gross * scale;
  const goLive = Math.max(0, model.goLiveMonths || 0);
  const ramp = Math.max(0, model.ramp || 0);
  let prev = -oneTime;
  for (let m = 1; m <= model.horizonYears * 12; m += 1) {
    const since = m - goLive;
    const next = prev + (since <= 0 ? 0 : (gross / 12) * (ramp ? Math.min(1, since / ramp) : 1)) - (since > 0 || model.licenseFromSigning ? monthlyCost : 0);
    if (next >= 0 && prev < 0) return m - 1 + -prev / (next - prev);
    prev = next;
  }
  return oneTime <= 0 ? 0 : null;
}

export interface ProposalDocumentProps {
  data: QuoteBuilder;
  layout: ProposalLayout;
  memo: BusinessCase;
  terms: Terms;
  timeline: Timeline;
  valueModel: ValueModel;
  /** YYYY-MM-DD. */
  prepared: string;
  /** No card frame: the print page. */
  bare?: boolean;
}

/**
 * The proposal document: every tab of the quote workspace, in the sections
 * and order chosen, with the appendix lettered after the main part. The same
 * document shows on the Proposal tab and prints from its own page.
 */
export function ProposalDocument({ data, layout, memo, terms, timeline, valueModel, prepared, bare = false }: ProposalDocumentProps): JSX.Element {
  const { quote, lines, totals, options } = data;
  const scope = quote.scope ?? { summary: '', rows: [] };
  const oneTime = totals.oneTime.net;
  const monthly = monthlyCostOf(data);
  const years = valueModel.horizonYears;
  const hasValue = valueTotals(valueModel).gross > 0;
  const base = roiModel(valueModel, oneTime, monthly);
  const cases = [
    { key: 'cons', label: 'Conservative case', roi: roiModel(withScenario(valueModel, 'conservative'), oneTime, monthly) },
    { key: 'base', label: 'Expected case', roi: base },
    { key: 'best', label: 'Best case', roi: roiModel(withScenario(valueModel, 'stretch'), oneTime, monthly) },
  ];
  const payTxt = (p: number | null) => (p === null ? `Beyond ${years} years` : `${one1(p)} months`);
  const schedule = scheduleOf(timeline);
  const milestones = milestoneAmounts(terms, oneTime);
  const first = milestones.rows[0]?.amount ?? 0;
  const customer = terms.signatures.customerCompany || quote.account_name;
  const projectName = quote.name ?? quote.quote_number;
  const figures: Record<string, string> = {
    payback: payTxt(base.payback),
    consPayback: payTxt(cases[0]!.roi.payback),
    bestPayback: payTxt(cases[2]!.roi.payback),
    gross: money(round1k(base.totals.gross)),
    runNet: money(round1k(base.runNet)),
    one: money(oneTime),
    mon: money(monthly),
    year: money(monthly * 12),
    kickoff: timeline.start ? formatDate(timeline.start) : '',
    golive: schedule.goLive ? formatDate(schedule.goLive) : '',
    customer,
    ms1: money(first),
    rest: money(oneTime - first),
  };
  const rich = (value: string): ReactNode =>
    boldRuns(fillTokens(value, figures)).map((r, i) =>
      r.bold ? (
        <strong key={i} className="font-strong">
          {r.text}
        </strong>
      ) : (
        <span key={i}>{r.text}</span>
      ),
    );
  const para = (value: string, className = lead) => (value.trim() ? <p className={className}>{rich(value)}</p> : null);
  const bullets = (value: string) => {
    const items = linesOf(value);
    return items.length ? <ProposalList items={items.map(rich)} /> : null;
  };
  const textRows = <T extends object>(rows: T[], cols: { key: keyof T & string; label: string; width?: string }[]) => {
    const at = (r: T, k: keyof T & string) => String(r[k] ?? '');
    const kept = rows.filter((r) => at(r, cols[0]!.key).trim());
    return kept.length ? (
      <ProposalTable
        columns={cols.map((c, i) => ({ key: c.key, label: c.label, width: c.width, muted: i > 0 }))}
        rows={kept.map((r, i) => ({ key: String(i), cells: Object.fromEntries(cols.map((c) => [c.key, rich(at(r, c.key))])) }))}
      />
    ) : null;
  };
  const caseTable = (only?: string[]) => {
    const rows = [
      { key: 'gross', label: 'Annual benefit at full run rate', get: (r: typeof base) => money(r.totals.gross) },
      { key: 'runNet', label: 'Net return each year once live', get: (r: typeof base) => money(r.runNet) },
      { key: 'payback', label: 'Payback from kickoff', get: (r: typeof base) => payTxt(r.payback) },
      { key: 'net', label: `${years}-year net return`, get: (r: typeof base) => money(r.net) },
      ...(valueModel.ebitdaMultiple
        ? [{ key: 'ev', label: 'Added enterprise value', get: (r: typeof base) => money(Math.max(0, r.runNet) * valueModel.ebitdaMultiple) }]
        : []),
    ].filter((r) => !only || only.includes(r.key));
    return (
      <ProposalTable
        columns={[{ key: 'label', label: '' }, ...cases.map((c) => ({ key: c.key, label: c.label, align: 'right' as const, highlight: c.key === 'base' }))]}
        rows={rows.map((r) => ({ key: r.key, cells: { label: r.label, ...Object.fromEntries(cases.map((c) => [c.key, r.get(c.roi)])) } }))}
      />
    );
  };

  // The investment table, as the Investment table settings have it.
  const inv = layout.investment;
  const per = inv.annual ? '/ yr' : '/ mo';
  const priceColumns = [
    ...(inv.listPrice ? [{ key: 'list', label: 'List price' }] : []),
    ...(inv.discount ? [{ key: 'discount', label: 'Discount' }] : []),
    ...(inv.price || (!inv.listPrice && !inv.discount) ? [{ key: 'price', label: 'Price' }] : []),
  ];
  // One kind of thing sold (only services, say): no category band or subtotals, just the lines and the totals.
  const lineSections = orderSections(lines, [], money, { annual: inv.annual });
  const oneKind = lineSections.length <= 1;
  const lineTable = (
    <QuoteLines
      sections={lineSections}
      priceColumns={priceColumns}
      hideQuantity={!inv.quantity}
      showSections={!oneKind}
      showGroups={inv.groups && !(oneKind && (lineSections[0]?.groups.length ?? 0) <= 1)}
      showLines={inv.lines}
      showSubtotals={inv.subtotals && !oneKind}
    />
  );
  const linesSectionOn = layout.sections.some((s) => s.key === 'lines' && s.enabled);
  const discountParts = [
    totals.oneTime.included ? `${money(totals.oneTime.included)} included` : null,
    totals.oneTime.lineDiscount + totals.oneTime.discount ? `${money(totals.oneTime.lineDiscount + totals.oneTime.discount)} one-time` : null,
    totals.monthly.lineDiscount + totals.monthly.discount
      ? inv.annual
        ? `${money((totals.monthly.lineDiscount + totals.monthly.discount) * 12)} / yr`
        : `${money(totals.monthly.lineDiscount + totals.monthly.discount)} / mo × 12`
      : null,
  ].filter(Boolean);

  // What it costs by kind: recurring services (a retainer) are services, not a license.
  const cost = investmentSummary(data);
  const monthName = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const termText = (l: (typeof cost.recurring.lines)[number]) =>
    l.months === null ? 'Month to month' : `${l.months} ${l.months === 1 ? 'month' : 'months'}${l.start && l.end ? `, ${monthName(l.start.slice(0, 7))} through ${monthName(l.end.slice(0, 7))}` : ''}`;
  const payInline = inv.withPayment && layout.sections.some((s) => s.key === 'investment' && s.enabled);

  // Payment terms: implementation milestones, the recurring services' invoices, and software billing.
  const paymentTerms = (): ReactNode => {
      const net = terms.payment.netDays;
      const invoices = recurringInvoices(data, net);
      const license = lines.find((l) => l.family === 'software' && l.billing === 'monthly' && !l.parent_line_id)?.name ?? 'Software license';
      return oneTime > 0 || cost.recurring.monthly > 0 || cost.softwareMonthly > 0 ? (
        <div className="flex flex-col">
          {oneTime > 0 && milestones.rows.length ? (
            <>
              <h3 className={`${subhead} mt-0`}>Implementation</h3>
              <ProposalTable
                columns={[
                  { key: 'name', label: 'Milestone' },
                  { key: 'pct', label: 'Share', align: 'right', width: '14%' },
                  { key: 'amount', label: 'Amount', align: 'right', width: '20%' },
                  ...(net ? [{ key: 'due', label: 'Due', align: 'right' as const, width: '14%' }] : []),
                ]}
                rows={[
                  ...milestones.rows.map((m) => ({ key: m.id, cells: { name: m.name, pct: `${Math.round(m.pct)}%`, amount: money(m.amount), due: `Net ${net}` } })),
                  { key: 'total', total: true, cells: { name: 'Total', pct: `${Math.round(milestones.totalPct)}%`, amount: money((oneTime * milestones.totalPct) / 100), due: '' } },
                ]}
              />
            </>
          ) : null}
          {cost.recurring.monthly > 0 ? (
            <>
              <h3 className={subhead}>{payInline ? 'Payment terms' : 'Recurring services'}</h3>
              {invoices.length ? (
                <ProposalTable
                  columns={[
                    { key: 'date', label: 'Invoiced' },
                    { key: 'for', label: 'For' },
                    { key: 'amount', label: 'Amount', align: 'right', width: '20%' },
                    ...(net ? [{ key: 'due', label: 'Due', align: 'right' as const, width: '20%' }] : []),
                  ]}
                  rows={[
                    ...invoices.map((i) => ({ key: i.date, cells: { date: formatDate(i.date), for: monthName(i.month), amount: money(i.amount), due: formatDate(i.due) } })),
                    { key: 'total', total: true, cells: { date: 'Total', for: '', amount: money(invoices.reduce((t, i) => t + i.amount, 0)), due: '' } },
                  ]}
                />
              ) : (
                <ProposalTable
                  columns={[{ key: 'k', label: '', width: '30%' }, { key: 'v', label: '' }]}
                  rows={[
                    { key: 'f', cells: { k: 'Fee', v: `${money(cost.recurring.monthly)} a month` } },
                    { key: 'b', cells: { k: 'Billing', v: 'Monthly in advance' } },
                    ...(net ? [{ key: 'd', cells: { k: 'Due', v: `Net ${net}` } }] : []),
                  ]}
                />
              )}
            </>
          ) : null}
          {cost.softwareMonthly > 0 ? (
            <>
              <h3 className={subhead}>Software</h3>
              <ProposalTable
                columns={[{ key: 'k', label: '', width: '30%' }, { key: 'v', label: '' }]}
                rows={[
                  { key: 'l', cells: { k: license, v: `${money(cost.softwareMonthly)} a month` } },
                  { key: 'b', cells: { k: 'Billing', v: `${terms.payment.billing === 'annual' ? 'Annually' : 'Monthly'} in advance, starting at ${valueModel.licenseFromSigning ? 'signing' : 'go-live'}` } },
                  ...(net ? [{ key: 'd', cells: { k: 'Due', v: `Net ${net}` } }] : []),
                ]}
              />
            </>
          ) : null}
          {terms.payment.note ? <p className={`${note} mt-3`}>{terms.payment.note}</p> : null}
        </div>
      ) : null;
  };

  const sections: Record<SectionKey, () => ReactNode> = {
    contents: () => null,
    summary: () =>
      scope.summary || hasValue ? (
        <div className="flex flex-col gap-3">
          {parseBody(scope.summary).map((b, i) => (b.kind === 'paragraph' ? <p key={i} className={lead}>{b.text}</p> : <ProposalList key={i} items={b.items} />))}
          {hasValue ? (
            <ProposalList
              className="text-[15px]"
              items={roiLead(valueModel, base, monthly).map((l) => (
                <>
                  <strong className="font-strong">{l.bold}</strong>
                  {l.rest}
                </>
              ))}
            />
          ) : null}
        </div>
      ) : null,
    included: () => {
      const inc = scope.rows.filter((r) => r.included);
      const exc = scope.rows.filter((r) => !r.included);
      return inc.length ? (
        <div className="flex flex-col gap-3">
          <ProposalTable
            columns={[{ key: 'theme', label: layout.includedColumns.theme, width: '28%' }, { key: 'covers', label: layout.includedColumns.covers, muted: true }]}
            rows={inc.map((r) => ({ key: r.id, cells: { theme: r.theme, covers: r.covers } }))}
          />
          {exc.length ? <p className={note}>Not included: {exc.map((r) => r.theme).join(', ')}.</p> : null}
        </div>
      ) : null;
    },
    timeline: () => {
      if (!schedule.phases.length) return null;
      const last = schedule.phases.length - 1;
      return (
        <div className="flex flex-col gap-3">
          <p className={note}>
            {schedule.weeks} weeks from kickoff{schedule.goLive ? `, going live ${formatDate(schedule.goLive)}` : ''}.
          </p>
          <PhaseTimeline
            phases={schedule.phases.map((p) => ({ key: p.id, when: `${p.weeks} ${Number(p.weeks) === 1 ? 'week' : 'weeks'}`, name: p.name, description: p.description }))}
            milestones={[
              { key: 'kickoff', label: 'Kickoff', date: timeline.start ? formatDate(timeline.start) : '—' },
              ...schedule.phases.map((p, i) => ({
                key: p.id,
                label: i === last - 1 ? `${p.name} complete · Go-live` : `${p.name} complete`,
                date: p.to ? formatDate(p.to) : '—',
              })),
            ]}
          />
        </div>
      );
    },
    options: () =>
      options.length > 1 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {options.map((o, i) => {
            const one = o.one_time_net ?? 0;
            const mon = o.monthly_net ?? 0;
            const off = Math.max(0, (o.one_time_list ?? 0) - one + ((o.monthly_list ?? 0) - mon) * 12);
            return (
              <OptionCard
                key={o.id}
                kicker={`Option ${i + 1}`}
                name={o.name ?? o.quote_number}
                recommended={o.is_primary}
                price={money(one + mon * 12)}
                per="grand total"
                then="One-time services + 12 months of software"
                rows={[
                  { key: 'one', label: 'One-time services', value: money(one) },
                  { key: 'mon', label: 'Software', value: `${money(mon)} / mo` },
                  { key: 'disc', label: 'Total discount', value: money(off), muted: off === 0 },
                ]}
              />
            );
          })}
        </div>
      ) : null,
    investment: () => {
      const invoices = recurringInvoices(data, terms.payment.netDays);
      if (inv.scheduleOnly && invoices.length) {
        // A retainer: the invoice schedule is the investment, with anything
        // gifted (a complimentary line) listed at no charge.
        // A gifted line with a customer note is marked *, **, ... and its note
        // printed under the table; one without a note says "(gifted)".
        const firstDate = invoices[0]!.date;
        const gifted = data.lines
          .filter((l) => l.inclusion === 'complimentary' && !l.parent_line_id)
          .sort((a, b) => (a.start_date ?? firstDate).localeCompare(b.start_date ?? firstDate));
        const noted = gifted.filter((l) => l.customer_note?.trim());
        const mark = (l: (typeof gifted)[number]) => '*'.repeat(noted.indexOf(l) + 1);
        return (
          <div className="flex flex-col">
            <ProposalTable
              columns={[
                { key: 'date', label: 'Date' },
                { key: 'for', label: 'For' },
                { key: 'amount', label: 'Amount', align: 'right', width: '24%' },
              ]}
              rows={[
                ...gifted.map((l) => ({
                  key: l.id,
                  cells: {
                    date: `${formatDate(l.start_date ?? firstDate)}${mark(l)}`,
                    for: noted.includes(l) ? l.name : `${l.name} (gifted)`,
                    amount: money(0),
                  },
                })),
                ...invoices.map((i) => ({ key: i.date, cells: { date: formatDate(i.date), for: monthName(i.month), amount: money(i.amount) } })),
                { key: 'total', total: true, cells: { date: 'Total', for: '', amount: money(invoices.reduce((t, i) => t + i.amount, 0)) } },
              ]}
            />
            {noted.map((l) => (
              <p key={l.id} className={`${note} mt-3`}>
                {mark(l)} {l.customer_note}
              </p>
            ))}
            {terms.payment.note ? <p className={`${note} mt-3`}>{terms.payment.note}</p> : null}
          </div>
        );
      }
      return (
      <div className="flex flex-col">
        {linesSectionOn ? null : lineTable}
        <TotalsStack
          rows={[
            ...(oneTime > 0 ? [{ key: 'one', label: 'Services (one-time)', value: money(oneTime) }] : []),
            ...(totals.oneTime.included ? [{ key: 'inc', label: 'Included at no charge', value: money(totals.oneTime.included) }] : []),
            ...(cost.recurring.monthly > 0 ? [{ key: 'rec', label: 'Recurring services (monthly)', value: money(cost.recurring.monthly), per: '/ mo' }] : []),
            ...(cost.recurring.total !== null && cost.recurring.monthly > 0
              ? [{ key: 'term', label: 'Total over the term', sublabel: cost.recurring.lines.map(termText).join('; '), value: money(cost.recurring.total), total: oneTime === 0 && cost.softwareMonthly === 0 }]
              : []),
            ...(cost.softwareMonthly > 0
              ? [{ key: 'mon', label: `Software license (${inv.annual ? 'annual' : 'monthly'})`, value: money(cost.softwareMonthly * (inv.annual ? 12 : 1)), per }]
              : []),
            ...(inv.totalDiscount && totals.firstYear.discount > 0
              ? [{ key: 'disc', label: 'Total discount', sublabel: discountParts.join(' + '), value: money(totals.firstYear.discount), total: true }]
              : []),
          ]}
        />
        {payInline ? paymentTerms() : null}
      </div>
      );
    },
    lines: () => (lines.length ? lineTable : null),
    payment: () => (payInline ? null : paymentTerms()),
    roi: () =>
      hasValue ? (
        <div className="flex flex-col gap-4">
          <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-4">
            <StatTile className={tile} label="Payback" value={payTxt(base.payback)} note="From kickoff, including the build" />
            <StatTile className={tile} label="Net return each year once live" value={money(base.runNet)} />
            <StatTile className={tile} label={`${years}-year net return`} value={money(base.net)} note={`${one1(base.multiple)}x return on cost`} />
            <StatTile
              className={tile}
              label="Added enterprise value"
              value={valueModel.ebitdaMultiple ? money(base.runNet * valueModel.ebitdaMultiple) : '—'}
              note={valueModel.ebitdaMultiple ? `At ${valueModel.ebitdaMultiple}x EBITDA` : undefined}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <LineChart
              title="Cumulative net value"
              data={base.cumulative.map((v, m) => ({ month: m === 0 ? 'Start' : `Mo ${m}`, net: Math.round(v) }))}
              config={{ net: { label: 'Cumulative net' } }}
              xKey="month"
              series={['net']}
              valueFormatter={(v) => formatCurrency(v)}
              height={240}
              referenceLines={Math.round(base.goLive) > 0 ? [{ x: `Mo ${Math.round(base.goLive)}`, label: 'Launch' }] : []}
              markers={
                base.payback !== null && base.payback <= base.months && base.payback > 0
                  ? [{ x: `Mo ${Math.round(base.payback)}`, y: Math.round(base.cumulative[Math.round(base.payback)] ?? 0), label: `Break-even · month ${one1(base.payback)}` }]
                  : []
              }
            />
            <BarList
              items={base.totals.byDriver.map((d) => ({ key: d.label, label: d.label, note: d.type, value: Math.round(d.value) }))}
              valueFormatter={(v) => formatCurrency(v)}
            />
          </div>
          <div>
            <h3 className={subhead}>Conservative, expected, and best case</h3>
            {caseTable(['runNet', 'payback', 'net'])}
          </div>
        </div>
      ) : null,
    beyond: () =>
      valueModel.quals.length ? (
        <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-3">
          {valueModel.quals.map((q) => (
            <div key={q.id} className="rounded-[var(--radius-lg)] border border-[var(--border)] px-3 py-2.5">
              <div className="text-[13px] font-emphasis text-[var(--fg)]">{q.benefit}</div>
              <div className="text-[12.5px] text-[var(--fg-muted)]">{q.detail}</div>
            </div>
          ))}
        </div>
      ) : null,
    assumptions: () => {
      if (!valueModel.assumptions.length) return null;
      const method = linesOf(memo.method);
      const table = (
        <ProposalTable
          columns={[
            { key: 'label', label: 'Assumption', width: '30%' },
            { key: 'basis', label: 'Basis', muted: true },
            { key: 'impact', label: 'Annual impact', align: 'right', width: '16%' },
          ]}
          rows={valueModel.assumptions.map((a) => {
            const m = valueModel.metrics.find((x) => x.id === a.metric);
            return { key: a.id, cells: { label: a.label, basis: [m ? `${m.name}: ` : '', a.note].join(''), impact: money(impactOf(a, valueModel.metrics)) } };
          })}
        />
      );
      return method.length ? (
        <div className="flex flex-col">
          <h3 className={`${subhead} mt-0`}>How we got these numbers</h3>
          <ProposalList items={method.map(rich)} />
          <h3 className={subhead}>The assumptions</h3>
          {table}
        </div>
      ) : (
        table
      );
    },
    foundation: () => {
      // Shown when the order licenses Foundation.
      if (!lines.some((l) => l.family === 'software' && !l.parent_line_id)) return null;
      const muted = 'flex flex-col gap-1 text-[12.5px] leading-[1.5] text-[var(--fg-muted)]';
      return (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-2xl font-light text-[var(--fg)]">Your system&apos;s head start.</p>
          <p className={lead}>
            Foundation is Olytic&apos;s proprietary, composable software engine. It supplies the groundwork every business application needs, so your build starts with
            that groundwork in place and the engagement is spent on your workflow. Olytic runs it for you, and one flat monthly license covers your whole team.
          </p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              ['Fully custom', "Your operations shape the data model, workflows, and interface. Nothing is bent to fit a vendor's template."],
              ['Product-grade quality', 'A complete design system gives every screen the consistency and finish of commercial software.'],
              ['Live in weeks', 'With the groundwork already built, your application can go live in weeks.'],
              ['Lower cost', 'Less to build up front, and a license that stays flat as your team grows.'],
            ].map(([title, body]) => (
              <div key={title} className={muted}>
                <span className="text-[11px] font-strong uppercase tracking-[0.12em] text-[var(--accent)]">{title}</span>
                <span>{body}</span>
              </div>
            ))}
          </div>
          <h3 className={subhead}>What Foundation includes</h3>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              ['Application engine', 'The groundwork every business application needs, already built and run for you.', ['Records and permissions', 'Environments and activity history', 'Hosting, backups, and monitoring']],
              ['Design system', 'Polished interface components, refined across every application we deliver.', ['Lists, records, and dashboards', 'Approvals and activity timelines', 'Layouts for phones and desktops']],
              ['AI administration', 'Model access, budgets, and controls for your team, with monthly AI credits included.', ["Choose your team's AI models", 'Set budgets and spending alerts', 'Control access person by person']],
            ].map(([title, body, items]) => (
              <div key={title as string} className={muted}>
                <span className="text-[13px] font-strong text-[var(--fg)]">{title}</span>
                <span>{body}</span>
                <ProposalList className="text-[12.5px]" items={items as string[]} />
              </div>
            ))}
          </div>
          <div className="mt-2 grid gap-6 md:grid-cols-2">
            <div className={muted}>
              <span className="text-[13px] font-strong text-[var(--fg)]">Run for you</span>
              <ProposalList
                className="text-[12.5px]"
                items={[
                  'Hosting, monitoring, and capacity managed by Olytic',
                  'Daily backups kept for 30 days, with restores tested',
                  'Security patches applied, promptly for critical issues',
                  'Certificates, domains, and connections to your other systems maintained',
                ]}
              />
            </div>
            <div className={muted}>
              <span className="text-[13px] font-strong text-[var(--fg)]">A flat license for your whole team</span>
              <span>
                One monthly rate, whatever your headcount. Add people as your team grows without adding to the bill. AI usage beyond the included credits is
                metered, with budgets and alerts to keep it predictable. You can move up a tier at any time.
              </span>
            </div>
          </div>
        </div>
      );
    },
    signatures: () => {
      const s = terms.signatures;
      const text = [s.text, s.validDays > 0 ? `This proposal is valid for ${s.validDays} days from the date it was prepared.` : ''].filter(Boolean).join(' ');
      return (
        <div className="flex flex-col gap-3">
          {text ? <p className={note}>{text}</p> : null}
          <div className="grid gap-8 md:grid-cols-2">
            {[
              [s.customerCompany, s.customerName, s.customerTitle],
              [s.providerCompany, s.providerName, s.providerTitle],
            ].map(([org, name, title], i) => (
              <div key={i}>
                <p className="m-0 mb-1.5 text-sm font-strong text-[var(--fg)]">{org}</p>
                {[
                  ['Signature', ''],
                  ['Name', name],
                  ['Title', title],
                  ['Date', ''],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-end gap-3 border-b border-[var(--border-strong)] pb-1 pt-4 text-[13px]">
                    <span className="w-[70px] text-[10.5px] uppercase tracking-[0.12em] text-[var(--fg-muted)]">{label}</span>
                    <span className="font-emphasis text-[var(--fg)]">{value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    },
    ask: () => {
      const yearly = monthly * 12;
      const line = memo.askLine.trim()
        ? rich(memo.askLine)
        : oneTime || yearly
          ? (
              <>
                Approve {oneTime ? <><strong className="font-strong">{money(oneTime)}</strong> to implement</> : null}
                {oneTime && yearly ? ' and ' : null}
                {yearly ? <><strong className="font-strong">{money(yearly)} a year</strong> to run</> : null} {projectName}.
              </>
            )
          : `Approve ${projectName}, at no additional cost.`;
      return (
        <div className="flex flex-col gap-3">
          <p className="m-0 max-w-3xl text-xl font-light leading-[1.45] text-[var(--fg)]">{line}</p>
          {para(memo.recommendation)}
          <AskGrid
            items={[
              { key: 'one', label: 'Implementation', value: money(oneTime) },
              { key: 'lic', label: 'License', value: monthly ? `${money(monthly)} / mo` : '—' },
              { key: 'pay', label: 'Payback', value: hasValue ? payTxt(base.payback) : '—' },
              { key: 'live', label: 'Go-live', value: figures.golive || '—' },
              { key: 'by', label: 'Decision by', value: memo.decideBy || '—' },
            ]}
          />
          {memo.owner ? <p className={note}>Owner at {customer}: {memo.owner}</p> : null}
          {textRows(memo.stages, [
            { key: 'stage', label: 'Stage', width: '16%' },
            { key: 'what', label: 'What happens' },
            { key: 'cost', label: 'Cost', width: '18%' },
            { key: 'decision', label: 'Decision', width: '30%' },
          ])}
        </div>
      );
    },
    problem: () =>
      memo.problem.trim() || valueModel.metrics.length ? (
        <div className="flex flex-col gap-3">
          {para(memo.problem)}
          {valueModel.metrics.length ? (
            <ProposalTable
              columns={[{ key: 'name', label: 'What it costs today' }, { key: 'today', label: 'Today', align: 'right' }]}
              rows={valueModel.metrics.map((m) => ({ key: m.id, cells: { name: m.name, today: fmtMetric(m) } }))}
            />
          ) : null}
        </div>
      ) : null,
    solution: () =>
      linesOf(memo.solution).length || linesOf(memo.changes).length ? (
        <div className="flex flex-col">
          {bullets(memo.solution)}
          {linesOf(memo.changes).length ? (
            <>
              <h3 className={subhead}>What changes day to day</h3>
              {bullets(memo.changes)}
            </>
          ) : null}
        </div>
      ) : null,
    risks: () => textRows(memo.risks, [{ key: 'risk', label: 'Risk', width: '34%' }, { key: 'mitigation', label: "How we'll handle it" }]),
    alternatives: () =>
      textRows(memo.alternatives, [
        { key: 'option', label: 'Option', width: '22%' },
        { key: 'cost', label: 'Cost', width: '22%' },
        { key: 'time', label: 'Time to value', width: '14%' },
        { key: 'why', label: 'Why not' },
      ]),
    measures: () => {
      const rows = valueModel.assumptions.flatMap((a) => {
        const m = valueModel.metrics.find((x) => x.id === a.metric);
        if (!m) return [];
        const target = a.lever === 'capture' ? `${a.change}% less, ${a.capture ?? 100}% of it put to work` : `${a.change}% lower`;
        return [{ key: a.id, cells: { name: m.name, today: fmtMetric(m), target, worth: money(impactOf(a, valueModel.metrics)) } }];
      });
      if (!rows.length) return null;
      return (
        <div className="flex flex-col">
          <ProposalTable
            columns={[
              { key: 'name', label: 'Measure' },
              { key: 'today', label: 'Today', align: 'right' },
              { key: 'target', label: memo.measureBy ? `Target by ${memo.measureBy}` : 'Target', align: 'right' },
              { key: 'worth', label: 'Worth a year', align: 'right' },
            ]}
            rows={rows}
          />
          {linesOf(memo.checkpoints).length ? (
            <>
              <h3 className={subhead}>Checkpoints</h3>
              {bullets(memo.checkpoints)}
            </>
          ) : null}
        </div>
      );
    },
    finance: () => {
      if (!hasValue) return null;
      // How much of the expected benefit has to come true to pay back within 24 months.
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 40; i += 1) {
        const mid = (lo + hi) / 2;
        const p = paybackAt(valueModel, oneTime, monthly, mid);
        if (p !== null && p <= 24) hi = mid;
        else lo = mid;
      }
      const yearCols: ProposalTableColumn[] = [
        { key: 'year', label: '' },
        { key: 'one', label: 'Implementation', align: 'right' },
        { key: 'lic', label: 'License', align: 'right' },
        { key: 'ben', label: 'Benefit', align: 'right' },
        { key: 'net', label: 'Net', align: 'right' },
        { key: 'cum', label: 'Cumulative', align: 'right' },
      ];
      return (
        <div className="flex flex-col">
          {caseTable()}
          <p className={`${note} mt-3`}>
            <strong className="font-strong text-[var(--fg)]">What has to be true:</strong> benefits need to reach about {Math.round(hi * 100)}% of the expected case for
            the project to pay back within 24 months. The conservative case uses the low end of every assumption and the best case the high end; the expected case is the
            most likely outcome.
          </p>
          <h3 className={subhead}>Cash and benefit by year</h3>
          <ProposalTable
            columns={yearCols}
            rows={roiByYear(valueModel, oneTime, monthly, base).map((y) => ({
              key: String(y.year),
              cells: { year: `Year ${y.year}`, one: money(y.oneTime), lic: money(y.software), ben: money(y.benefit), net: money(y.net), cum: money(y.cumulative) },
            }))}
          />
          {memo.accounting.trim() ? <p className={`${note} mt-3`}>{rich(memo.accounting)}</p> : null}
        </div>
      );
    },
    roles: () =>
      linesOf(memo.rolesOlytic).length || linesOf(memo.rolesCustomer).length ? (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className={`${subhead} mt-0`}>Olytic</h3>
            {bullets(memo.rolesOlytic)}
          </div>
          <div>
            <h3 className={`${subhead} mt-0`}>{customer}</h3>
            {bullets(memo.rolesCustomer)}
          </div>
        </div>
      ) : null,
    exit: () => (linesOf(memo.exit).length ? <div className="flex flex-col gap-2">{linesOf(memo.exit).map((l, i) => <p key={i} className={lead}>{rich(l)}</p>)}</div> : null),
    it: () =>
      memo.it.some((r) => r.topic.trim()) ? (
        <div className="flex flex-col gap-3">
          {para(memo.itIntro)}
          {textRows(memo.it, [{ key: 'topic', label: 'Topic', width: '24%' }, { key: 'answer', label: 'How it works' }])}
        </div>
      ) : null,
  };

  const bodyOf = (ref: SectionRef): ReactNode => {
    if (ref.key.startsWith('custom:')) {
      const custom = layout.custom.find((c) => `custom:${c.id}` === ref.key);
      return custom && custom.body.trim() ? (
        <div className="flex flex-col gap-2.5">
          {parseBody(custom.body).map((b, i) => (b.kind === 'paragraph' ? <p key={i} className={lead}>{b.text}</p> : <ProposalList key={i} items={b.items} />))}
        </div>
      ) : null;
    }
    return sections[ref.key as SectionKey]();
  };
  const articleRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const before = () => articleRef.current && fitForPrint(articleRef.current);
    const after = () => articleRef.current && clearPrintFit(articleRef.current);
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);
  const built = layout.sections
    .filter((s) => s.enabled)
    .map((ref) => ({ ref, title: titleOf(ref, layout), body: ref.key === 'contents' ? true : bodyOf(ref) }))
    .filter((s) => s.body);
  const main = built.filter((s) => !inAppendix(s.ref));
  const appendix = built.filter((s) => inAppendix(s.ref));
  const id = (ref: SectionRef) => `ps-${ref.key.replace(':', '-')}`;
  const letter = (i: number) => String.fromCharCode(65 + i);
  const breaksBefore = new Set(['options', 'foundation']);
  // Signatures and custom sections (a work order, a note) print on one page.
  const keepWhole = (ref: SectionRef) => ref.key === 'signatures' || ref.key.startsWith('custom:');
  const title = (s: (typeof built)[number]) =>
    s.ref.key === 'assumptions' && linesOf(memo.method).length
      ? 'Assumptions and methodology'
      : s.ref.key === 'investment' && payInline && !s.ref.title?.trim()
        ? 'Investment and payment terms'
        : s.title;

  return (
    <article ref={articleRef} className={bare ? 'proposal-doc flex flex-col gap-8' : 'proposal-doc flex flex-col gap-8 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] px-6 py-8'}>
      <ProposalHeader
        logo={
          <>
            <img src={olyticMark} alt="" className="h-7 w-auto" />
            <span className="text-sm tracking-[0.3em]">OLYTIC</span>
          </>
        }
        title={projectName}
        meta={
          <>
            {quote.kind === 'change_order' ? <div>Change order for {customer}</div> : <div>Prepared for {customer}</div>}
            <div>Prepared {formatDate(prepared)}</div>
          </>
        }
      />
      {main.map((s) =>
        s.ref.key === 'contents' ? (
          <ProposalContents
            key="contents"
            main={main.filter((x) => x.ref.key !== 'contents').map((x) => ({ key: x.ref.key, label: title(x), href: `#${id(x.ref)}` }))}
            appendix={appendix.map((x) => ({ key: x.ref.key, label: title(x), href: `#${id(x.ref)}` }))}
          />
        ) : (
          <ProposalSection key={s.ref.key} id={id(s.ref)} title={title(s)} pageBreak={breaksBefore.has(s.ref.key)} allowSplit={!keepWhole(s.ref)}>
            {s.body}
          </ProposalSection>
        ),
      )}
      {appendix.length ? (
        <div className="flex flex-col gap-8 border-t-2 border-[var(--fg)] pt-6 print:break-before-page">
          <p className="m-0 text-2xl font-light text-[var(--fg)]">Appendix</p>
          {appendix.map((s, i) => (
            <ProposalSection key={s.ref.key} id={id(s.ref)} title={`Appendix ${letter(i)}: ${title(s)}`} pageBreak={s.ref.key === 'foundation'} allowSplit>
              {s.body}
            </ProposalSection>
          ))}
        </div>
      ) : null}
    </article>
  );
}

